import React, { useEffect, useState } from 'react';

// Growth promos (Sam, Oct 15). Each piece renders only while its ops switch is ON.
export const PROMISE_LINE = 'Sweet Guarantee: not happy with a paid session? Your next one is free.';
export const SG_SHORT = 'Claim within 24 hours with one tap. Sweet Listeners pays your listener. Self-help listening, not therapy.';
// Sweet Treat (Sam, Oct 19 2026 5:43 AM): the free first session listeners give.
export const SWEET_TREAT_LINE = 'Your first session is free, any format, with listeners and teachers showing the Sweet Treat badge.';
export const LISTENER_REFUND_LINE = 'Sweet Guarantee: if a talker claims a free make-good session, you keep your full pay for the original session, and the listener they book for the make-good is paid by Sweet Listeners.';
export const TRUST_LINE = 'Private: your listener only sees the name on your profile. We never share your email or phone number with them.';

let cached = null;
let pending = null;
const loadStatus = () => {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    let ref = '';
    try {
      const q = new URLSearchParams(window.location.search).get('ref') || '';
      if (/^[0-9a-f-]{36}$/i.test(q)) window.sessionStorage.setItem('sl_invite_ref', q);
      ref = q || window.sessionStorage.getItem('sl_invite_ref') || '';
    } catch (e) {
      ref = '';
    }
    let promo = '';
    try {
      const pq = (new URLSearchParams(window.location.search).get('promo') || '').toLowerCase();
      if (/^[a-z0-9]{2,20}$/.test(pq)) window.sessionStorage.setItem('sl_promo', pq);
      promo = pq || window.sessionStorage.getItem('sl_promo') || '';
    } catch (e) {
      promo = '';
    }
    const qs = [ref ? `ref=${encodeURIComponent(ref)}` : '', promo ? `promo=${encodeURIComponent(promo)}` : ''].filter(Boolean).join('&');
    pending = fetch(`/api/growth/status${qs ? `?${qs}` : ''}`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(j => {
        cached = j || {};
        pending = null;
        return cached;
      });
  }
  return pending;
};
export const useGrowthStatus = () => {
  const [s, setS] = useState(cached);
  useEffect(() => {
    if (typeof fetch !== 'function') return undefined;
    let live = true;
    loadStatus().then(j => live && setS(j));
    return () => {
      live = false;
    };
  }, []);
  return s;
};

const box = { margin: '10px 0', padding: '10px 12px', borderRadius: 12, fontSize: 15, lineHeight: 1.4 };

/** Talker's first ever session only (guests count as first). Listener-only accounts never see it. */
export const FirstSessionPromise = ({ variant = 'booking', className, who = 'listener' }) => {
  const s = useGrowthStatus();
  if (!s?.refundPromise || (s.role === 'listen')) return null;
  if (variant === 'home') {
    return (
      <p className={className} data-growth-promise="home" style={{ margin: '6px 0 0', fontWeight: 600, color: '#2e7d4f' }}>
        ✓ {PROMISE_LINE}
      </p>
    );
  }
  if (variant === 'charge') {
    // Sits right under the "won't be charged until after your session" line on
    // the booking screen (Sam, Oct 19 2026). Same soft pink box, navy text.
    return (
      <p
        className={className}
        data-growth-promise="charge"
        style={{ margin: '6px 0 0', padding: '10px 12px', borderRadius: 14, border: '1px solid #ffd3e4', background: '#fff1f6', color: '#0b2b5b', fontSize: 15, lineHeight: '22px', textAlign: 'left' }}
      >
        <strong style={{ fontWeight: 800 }}>{PROMISE_LINE}</strong>{' '}
        <span style={{ color: '#33415c' }}>Any completed paid session: claim a free make-good session within 24 hours, one tap from your session page. <a href="/faq">Fine print</a>.</span>
      </p>
    );
  }
  return (
    <div className={className} data-growth-promise={variant} style={{ ...box, background: '#eef8f1', border: '1px solid #cfe9d8', color: '#1f5e3a' }}>
      <strong>✓ {PROMISE_LINE}</strong>
      <div style={{ fontSize: 13, marginTop: 2, color: '#3d6b50' }}>One tap from your session page within 24 hours after it ends. Your {who} is still paid.</div>
    </div>
  );
};

const fmtDay = iso => new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });

/** Landing banner for a LISTENER's invite link (talkerBonus toggle; OFF on Live). Display name only. */
export const ListenerInviteBanner = ({ className }) => {
  const s = useGrowthStatus();
  const sweetBox = { ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', textAlign: 'center' };
  const days = x => (x && x.creditDays) || 30;
  if (s?.sweetTreat && !s.listener && !s.role) {
    // New Sweet Treat model: free first session, any format, listener-given.
    const who = s.inviter?.listener ? s.inviter : null;
    const src = s.promo ? 'true' : who ? 'invite' : 'signup';
    return (
      <div className={className} data-sweet-treat-banner={src} data-listener-invite={who ? 'true' : undefined} style={sweetBox}>
        <strong style={{ fontSize: 17 }}>{who ? `${who.name} invited you to Sweet Listeners` : 'Sweet Treat: your first session is free'}</strong>
        <div style={{ marginTop: 4 }}>{SWEET_TREAT_LINE} One free 15-minute session per person. Sign up to use it.</div>
        <div style={{ fontSize: 13, marginTop: 4, color: '#8a4a6a' }}>Use it within {days(s.promo || who || s.signupTreat)} days of signing up.</div>
      </div>
    );
  }
  if (s?.promo && !s.inviter && !s.listener && !(s.credits || []).length) {
    return (
      <div className={className} data-sweet-treat-banner="true" style={{ ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', textAlign: 'center' }}>
        <strong style={{ fontSize: 17 }}>Your Sweet Treat: $10 off your first chat</strong>
        <div style={{ marginTop: 4 }}>A 15-min text chat is free, or take $10 off a voice or video call. Sign up to claim it.</div>
        <div style={{ fontSize: 13, marginTop: 4, color: '#8a4a6a' }}>Use it within {s.promo.creditDays || 30} days of signing up.</div>
      </div>
    );
  }
  // Every new talker signup gets the Sweet Treat (Sam, Oct 16 11:37 AM): signed-out visitors, no link needed.
  if (s?.signupTreat && !s.inviter && !s.role && !s.listener && !(s.credits || []).length) {
    return (
      <div className={className} data-sweet-treat-banner="signup" style={{ ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', textAlign: 'center' }}>
        <strong style={{ fontSize: 17 }}>New here? Your Sweet Treat: $10 off your first chat</strong>
        <div style={{ marginTop: 4 }}>Every new talker gets one. A 15-min text chat is free, or take $10 off a voice or video call. Sign up to claim it.</div>
        <div style={{ fontSize: 13, marginTop: 4, color: '#8a4a6a' }}>Use it within {s.signupTreat.creditDays || 30} days of signing up.</div>
      </div>
    );
  }
  if (!s?.inviter?.listener || s.listener) return null;
  return (
    <div className={className} data-listener-invite="true" style={{ ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', textAlign: 'center' }}>
      <strong style={{ fontSize: 17 }}>{s.inviter.name} invited you to Sweet Listeners</strong>
      <div style={{ marginTop: 4 }}>
        Sign up for your <strong>Sweet Treat: $10 off your first chat</strong>, so a 15-min text chat is free. Or take $10 off a voice or video call.
      </div>
      <div style={{ fontSize: 13, marginTop: 4, color: '#8a4a6a' }}>Use it within {s.inviter.creditDays || 30} days of signing up.</div>
    </div>
  );
};

/** Signed-in talker with a $10 referral credit: value + expiry, wherever booking shows. */
export const ReferralCreditNote = ({ className }) => {
  const s = useGrowthStatus();
  const c = (s?.credits || []).find(x => x.from !== 'refund');
  if (!c || s.listener) return null;
  return (
    <div className={className} data-referral-credit="true" style={{ ...box, background: '#eef8f1', border: '1px solid #cfe9d8', color: '#1f5e3a' }}>
      <strong>Your Sweet Treat: ${Number(c.valueUsd || 10).toFixed(0)} off your first chat. A 15-min text chat is free, or $10 off a voice or video call.</strong>
      {c.expiresAt ? <div style={{ fontSize: 14, marginTop: 2 }} data-referral-credit-expiry="true">Your Sweet Treat expires {fmtDay(c.expiresAt)}.</div> : null}
    </div>
  );
};

export const BookingTrustLine = ({ className, who = 'listener' }) => {
  const s = useGrowthStatus();
  if (!s?.trustLine) return <ReferralCreditNote />;
  return (
    <>
    <ReferralCreditNote />
    <p className={className} data-growth-trust="true" style={{ ...box, background: '#f4f6fb', border: '1px solid #dfe5f2', color: '#33415c', fontSize: 14 }}>
      🔒 {who === 'listener' ? TRUST_LINE : TRUST_LINE.replace('your listener', `your ${who}`)}
    </p>
    </>
  );
};

/** Completed session page (talker): ask within 24h; after ops approves, pick money back or a free session. */
const FMT = { chat: 'text chat', voice: 'voice call', video: 'video call' };
export const RefundPromiseRequest = ({ transactionId }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = `/api/growth/refund/${encodeURIComponent(transactionId || '')}`;
  useEffect(() => {
    if (!transactionId || typeof fetch !== 'function') return undefined;
    let live = true;
    fetch(url, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => live && setSt(j))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [transactionId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st) return null;
  const req = st.request;
  if (!st.eligible && !req) return null;
  const post = (path, body) => {
    setBusy(true);
    fetch(url + path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) })
      .then(r => r.json())
      .then(j => j && (j.request || j.eligible !== undefined) && setSt(j))
      .catch(() => {})
      .then(() => setBusy(false));
  };
  const btn = { background: '#2e7d4f', color: '#fff', border: 0, borderRadius: 10, padding: '9px 14px', fontWeight: 600, fontSize: 15, cursor: 'pointer', margin: '0 8px 8px 0' };
  const fmt = FMT[req?.format] || 'session';
  let body;
  if (!req) {
    body = (
      <>
        <strong>{PROMISE_LINE}</strong>
        <div style={{ fontSize: 14, margin: '4px 0 8px' }}>Not happy with this session? Get a free 15-min {fmt} with any listener. One tap, no form. Claim within 24 hours after it ended.</div>
        <button type="button" style={btn} disabled={busy} onClick={() => post('')} data-growth-refund-button="true">{busy ? 'Claiming…' : 'Claim my free Sweet Guarantee session'}</button>
        <SweetGuaranteeFinePrint variant="claim" />
      </>
    );
  } else if (req.status === 'approved') {
    body = (
      <>
        <strong>Approved! Your Sweet Guarantee session is ready to claim.</strong>
        <div style={{ fontSize: 14, margin: '4px 0 8px' }}>Your listener is still paid.</div>
        <button type="button" style={{ ...btn, background: '#c2185b' }} disabled={busy} onClick={() => post('/choose', { choice: 'credit' })} data-growth-refund-choice="credit">Get my free 15-min {fmt}</button>
      </>
    );
  } else if (req.status === 'refunded') {
    body = <strong>Done: your money is on its way back to your card (usually 5–10 business days).</strong>;
  } else if (req.status === 'credited') {
    body = (
      <>
        <strong>Sweet Guarantee claimed: you have a free 15-min {fmt}.</strong>
        <div style={{ fontSize: 14, marginTop: 4 }}>Book any listener and checkout shows FREE ($0.00). We emailed you the details.</div>
        <SweetGuaranteeFinePrint variant="claim" />
      </>
    );
  } else if (req.status === 'denied') {
    return null;
  } else {
    body = (
      <>
        <strong>Sweet Guarantee claim received.</strong>
        <div style={{ fontSize: 14, marginTop: 4 }}>You’ve claimed on more than half of your paid sessions, so our team will take a quick look first and may ask a few questions. We’ll email you when your free session is ready.</div>
      </>
    );
  }
  return (
    <div data-growth-refund-request={req ? req.status : 'open'} style={{ ...box, background: '#eef8f1', border: '1px solid #cfe9d8', color: '#1f5e3a' }}>
      {body}
    </div>
  );
};

/** Listener invite card add-on: bonus line + stats. */
export const InviteBonusLine = ({ className, tone }) => {
  const s = useGrowthStatus();
  if (!s?.talkerBonus || !s.listener) return null;
  const amt = `$${Number(s.bonusUsd || 10).toFixed(0)}`;
  const inv = s.invite || { joined: 0, finished: 0, earnedUsd: 0 };
  // Sam 8:13 AM: on the navy band all text is white/near-white; stats light gray (WCAG AA).
  const dark = tone === 'dark';
  const main = dark ? '#ffffff' : '#1f5e3a';
  const muted = dark ? '#d5dbea' : '#555';
  return (
    <div className={className} data-invite-bonus="true">
      <p style={{ margin: '6px 0 2px', fontWeight: 700, color: main }}>Earn {amt} when a friend you invite finishes their first paid session.</p>
      <p style={{ margin: '0 0 6px', fontWeight: 600, color: main }}>They get their first 15 minutes free.</p>
      <p style={{ margin: '0 0 6px', fontSize: 14, color: muted }} data-invite-stats="true">
        {inv.joined} joined · {inv.finished} finished first session · ${Number(inv.earnedUsd || 0).toFixed(0)} earned · ${Number(inv.monthEarnedUsd || 0).toFixed(0)} this month, paid on the 1st
      </p>
    </div>
  );
};

// ---------------------------------------------------- social proof (real counts) --
let socialCached = null;
let socialPending = null;
const loadSocial = () => {
  if (socialCached) return Promise.resolve(socialCached);
  if (!socialPending) {
    socialPending = fetch('/api/growth/social', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(j => {
        socialCached = j || {};
        socialPending = null;
        return socialCached;
      });
  }
  return socialPending;
};
export const useSocialProof = () => {
  const [s, setS] = useState(socialCached);
  useEffect(() => {
    if (typeof fetch !== 'function') return undefined;
    let live = true;
    loadSocial().then(j => live && setS(j));
    return () => {
      live = false;
    };
  }, []);
  return s;
};
/** Matcher for topic paths that browse would return ≥1 published listener for, or null (unknown → show all).
 * Same rule as browse results (listingMatchesPaths): same branch in either direction. */
export const useCoveredPaths = () => {
  const s = useSocialProof();
  const mine = Array.isArray(s?.listenerPaths) ? s.listenerPaths : null;
  if (!mine || !mine.length) return null;
  return { has: p => mine.some(m => m === p || m.startsWith(`${p}/`) || p.startsWith(`${m}/`)) };
};
export const pathCovered = (covered, path) => !covered || path === 'just-talk' || covered.has(path);

export const SocialProofLine = ({ className }) => {
  const s = useSocialProof();
  if (!s?.listeners) return null;
  const topics = s.topics > 1 ? ` across ${s.topicsLabel} topics` : '';
  return (
    <p className={className} data-social-proof="true" style={{ margin: '8px 0 0', fontWeight: 600, color: '#3a4a66' }}>
      {s.listenersLabel} listener{s.listeners === 1 ? '' : 's'}{topics}
      {s.conversationsWeek ? <span data-social-week="true"> · {s.conversationsWeekLabel} conversations this week</span> : null}
    </p>
  );
};

/** Checkout summary line for the $10 referral credit on voice/video (server applies it as line-item/referral-credit). */
const FORMAT_USD = { voice: 20, video: 30 };
export const ReferralCreditLine = ({ sessionFormat, totalPrice, className }) => {
  const s = useGrowthStatus();
  const c = (s?.credits || []).find(x => x.from !== 'refund');
  const full = FORMAT_USD[sessionFormat];
  const paid = Number(String(totalPrice || '').replace(/[^0-9.]/g, ''));
  if (!c || !full || !Number.isFinite(paid) || paid >= full) return null;
  return (
    <div className={className} data-referral-credit-line="true" style={{ color: '#1f5e3a', fontWeight: 600 }}>
      <span>Sweet Treat ($10 off)</span>
      <span>-$10.00</span>
    </div>
  );
};

/** Home: "Top Sweet Listeners this month" (top 3 by referred talkers with a completed paid session). Hidden under 3. */
export const TopSweetListeners = ({ className }) => {
  const s = useSocialProof();
  const top = Array.isArray(s?.topListeners) ? s.topListeners : [];
  if (top.length < 3) return null;
  return (
    <section className={className} data-top-sweet-listeners="true" style={{ margin: '18px 0', padding: '14px 16px', borderRadius: 14, background: '#fff4f8', border: '1px solid #f6cfe0', textAlign: 'center' }}>
      <strong style={{ fontSize: 18, color: '#7a2350' }}>Top Sweet Listeners this month</strong>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 10, flexWrap: 'wrap' }}>
        {top.slice(0, 3).map((t, i) => (
          <div key={i} style={{ width: 92 }} data-top-listener="true">
            {t.photo ? (
              <img src={t.photo} alt="" width={64} height={64} style={{ borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f6cfe0', margin: '0 auto', lineHeight: '64px', fontWeight: 700, color: '#7a2350' }}>{String(t.name || '?').slice(0, 1).toUpperCase()}</div>
            )}
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, color: '#2b2b3a', wordBreak: 'break-word' }}>{t.name}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/** Signup page note: every new talker gets the Sweet Treat (switch on + cap room only). */
export const SignupTreatNote = ({ className }) => {
  const s = useGrowthStatus();
  if (!s?.signupTreat || s.role) return null;
  if (s.signupTreat.free) {
    return (
      <p className={className} data-signup-sweet-treat="true" style={{ ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', fontSize: 15 }}>
        <strong>Sweet Treat for new talkers and learners:</strong> {SWEET_TREAT_LINE} Use it within {s.signupTreat.creditDays || 30} days.
      </p>
    );
  }
  return (
    <p className={className} data-signup-sweet-treat="true" style={{ ...box, margin: '0 0 12px', background: '#fff4f8', border: '1px solid #f6cfe0', color: '#7a2350', fontSize: 15 }}>
      <strong>Sweet Treat for new talkers and learners:</strong> $10 off your first chat. A 15-min text chat is free, or $10 off voice or video. Use it within {s.signupTreat.creditDays || 30} days.
    </p>
  );
};

/** Listener side of the first-session guarantee (Become a Listener, dashboard). */
export const ListenerRefundLine = ({ className }) => {
  const s = useGrowthStatus();
  if (!s?.refundPromise) return null;
  return (
    <p className={className} data-growth-listener-refund="true" style={{ ...box, background: '#f4f6fb', border: '1px solid #dfe5f2', color: '#0b2b5b', fontSize: 15 }}>
      <strong>{LISTENER_REFUND_LINE}</strong>
    </p>
  );
};

/** Sweet Guarantee fine print (Sam, Oct 30 2026). home + /talk/* near top, dashboard near bottom, claim box. Full terms on /faq. */
export const SweetGuaranteeFinePrint = ({ variant = 'home', className, buyerView }) => {
  const s = useGrowthStatus();
  if (!s?.refundPromise) return null;
  if (variant !== 'claim' && s.role === 'listen' && !buyerView) return null;
  const small = { fontSize: 12.5, lineHeight: '18px', color: variant === 'claim' ? '#1f5e3a' : '#4a5670' };
  return (
    <div className={className} data-sweet-guarantee-fineprint={variant} style={variant === 'claim' ? { marginTop: 6 } : { margin: '8px auto', maxWidth: 760, padding: '6px 12px', textAlign: 'center' }}>
      {variant !== 'claim' ? <strong style={{ ...small, fontWeight: 700, color: '#2e7d4f' }}>✓ {PROMISE_LINE} </strong> : null}
      <span style={small}>
        Fine print: completed paid sessions only; claim a free 15-min make-good session in the same format within 24 hours, one tap from your session page. No lifetime limit; if you claim on more than half of your paid sessions we may ask questions first, and abuse can close your account. Not for Community Guidelines endings, no-shows, or free sessions. Subject to a monthly budget. Sweet Listeners pays your listener. Self-help listening, not therapy or counseling. <a href="/faq" style={{ color: 'inherit', textDecoration: 'underline' }}>Full terms</a>
      </span>
    </div>
  );
};

/** Sweet Guarantee promo card (Sam, Oct 30 2026 8:48 PM): prominent headline + CTA on home and dashboard, with fine print. */
export const SweetGuaranteeBanner = ({ variant = 'home', className, buyerView }) => {
  const s = useGrowthStatus();
  if (!s?.refundPromise) return null;
  // Sam Nov 1: show on buyer/Chat & Learn dashboard even when saved role is listen (dual-role).
  if (s.role === 'listen' && !buyerView) return null;
  return (
    <section className={className} data-sweet-guarantee-banner={variant} style={{ margin: '12px auto', maxWidth: 640, padding: '10px 14px', borderRadius: 12, background: 'linear-gradient(135deg,#eaf7ef,#f5fbf7)', border: '1px solid #2e7d4f', textAlign: 'center', color: '#123d26' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#2e7d4f' }}>✓ The Sweet Guarantee</div>
      <h2 style={{ margin: '2px 0 2px', fontSize: 17, lineHeight: '22px', fontWeight: 800 }}>Not happy? Your next session is free.</h2>
      <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: '18px' }}>One tap within 24 hours of a paid session.</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <a href="/s?availableNow=only" data-sweet-guarantee-cta={variant} style={{ background: '#2e7d4f', color: '#fff', padding: '7px 14px', borderRadius: 999, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Talk to someone, risk-free</a>
        <a href="/faq" style={{ color: '#2e7d4f', padding: '7px 8px', fontSize: 13, fontWeight: 700, textDecoration: 'underline' }}>How the Sweet Guarantee works</a>
      </div>
    </section>
  );
};

// Oct 31 theme above-fold harden: one-line Sweet Guarantee (no fine print; terms via FAQ).
export const SweetGuaranteeLine = ({ variant = 'talk-hero' }) => {
  const s = useGrowthStatus();
  if (!s?.refundPromise) return null;
  if (s.role === 'listen') return null;
  return (
    <p data-sweet-guarantee-line={variant} style={{ margin: '12px auto 0', fontSize: 14, lineHeight: '19px', color: '#123d26', textAlign: 'center', maxWidth: 560 }}>
      <b style={{ color: '#2e7d4f' }}>✓ Sweet Guarantee:</b> not happy? Your next session is free.{' '}
      <a href="/faq" style={{ color: '#2e7d4f', fontWeight: 700, textDecoration: 'underline' }}>How it works</a>
    </p>
  );
};
